import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  CircularProgress,
  IconButton,
  Link,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import LanguageIcon from '@mui/icons-material/Language';
import RefreshIcon from '@mui/icons-material/Refresh';

const MICROLINK_PAUSE_PREFIX = 'shifty.microlink.pause.';

function pauseKey(url) {
  return `${MICROLINK_PAUSE_PREFIX}${url}`;
}

function readMicrolinkPaused(url) {
  if (!url || typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(pauseKey(url)) === '1';
  } catch {
    return false;
  }
}

function persistMicrolinkPause(url) {
  if (!url || typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(pauseKey(url), '1');
  } catch {
    /* quota / private mode */
  }
}

function clearMicrolinkPause(url) {
  if (!url || typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(pauseKey(url));
  } catch {
    /* */
  }
}

function normalizeUrl(raw) {
  const v = (raw || '').trim();
  if (!v) return '';
  if (/^https?:\/\//i.test(v)) return v;
  return `https://${v}`;
}

/**
 * Live-style homepage preview: tries Microlink screenshot (JSON API), then
 * thum.io image URL, then optional custom image. Many sites block third-party
 * capture; after one Microlink failure we stop calling their API until "Retry"
 * (avoids console noise and rate limits). For production you may swap in your
 * own Cloud Function + storage.
 */
export default function LiveHomepageThumbnail({
  homepageUrl,
  customImageUrl,
  height = 200,
  compact = false,
}) {
  const normalized = useMemo(() => normalizeUrl(homepageUrl), [homepageUrl]);
  const thumSrc = useMemo(() => {
    if (!normalized) return '';
    return `https://image.thum.io/get/width/1200/viewport/1200/noanimate/${encodeURIComponent(normalized)}`;
  }, [normalized]);

  const [microlinkUrl, setMicrolinkUrl] = useState(null);
  const [imgFailed, setImgFailed] = useState(false);
  const [loadingMicrolink, setLoadingMicrolink] = useState(true);
  /** Mirrors storage so the retry affordance shows without waiting for a refetch. */
  const [microlinkPausedUi, setMicrolinkPausedUi] = useState(false);
  const [microlinkAttempt, setMicrolinkAttempt] = useState(0);

  useEffect(() => {
    if (!normalized || (customImageUrl && customImageUrl.trim())) {
      setLoadingMicrolink(false);
      setMicrolinkUrl(null);
      setMicrolinkPausedUi(false);
      return undefined;
    }

    if (readMicrolinkPaused(normalized)) {
      setMicrolinkPausedUi(true);
      setLoadingMicrolink(false);
      setMicrolinkUrl(null);
      return undefined;
    }

    setMicrolinkPausedUi(false);

    const ac = new AbortController();
    setLoadingMicrolink(true);
    setMicrolinkUrl(null);
    setImgFailed(false);

    fetch(
      `https://api.microlink.io/?url=${encodeURIComponent(normalized)}&screenshot=true`,
      { signal: ac.signal },
    )
      .then(async (r) => {
        if (ac.signal.aborted) return;
        if (!r.ok) {
          persistMicrolinkPause(normalized);
          setMicrolinkPausedUi(true);
          return;
        }
        let j;
        try {
          j = await r.json();
        } catch {
          persistMicrolinkPause(normalized);
          setMicrolinkPausedUi(true);
          return;
        }
        if (ac.signal.aborted) return;
        const u = j?.data?.screenshot?.url;
        if (u) {
          clearMicrolinkPause(normalized);
          setMicrolinkPausedUi(false);
          setMicrolinkUrl(u);
        } else {
          persistMicrolinkPause(normalized);
          setMicrolinkPausedUi(true);
        }
      })
      .catch(() => {
        if (!ac.signal.aborted) {
          persistMicrolinkPause(normalized);
          setMicrolinkPausedUi(true);
        }
      })
      .finally(() => {
        if (!ac.signal.aborted) setLoadingMicrolink(false);
      });

    return () => ac.abort();
  }, [normalized, customImageUrl, microlinkAttempt]);

  const handleRetryMicrolink = useCallback(
    (e) => {
      e?.stopPropagation?.();
      clearMicrolinkPause(normalized);
      setMicrolinkPausedUi(false);
      setMicrolinkUrl(null);
      setImgFailed(false);
      setMicrolinkAttempt((a) => a + 1);
    },
    [normalized],
  );

  const primarySrc = (customImageUrl && customImageUrl.trim()) || microlinkUrl || thumSrc;

  const handleImgError = () => {
    if (microlinkUrl) {
      setMicrolinkUrl(null);
    } else {
      setImgFailed(true);
    }
  };

  useEffect(() => {
    setImgFailed(false);
  }, [primarySrc]);

  if (!normalized) {
    return (
      <Stack alignItems="center" justifyContent="center" sx={{ height, bgcolor: 'action.hover' }}>
        <LanguageIcon color="action" />
        <Typography variant="caption" color="text.secondary">No URL</Typography>
      </Stack>
    );
  }

  if (imgFailed && !((customImageUrl && customImageUrl.trim()))) {
    return (
      <Stack
        alignItems="center"
        justifyContent="center"
        spacing={1}
        sx={{ height, bgcolor: 'action.hover', px: 2, textAlign: 'center' }}
      >
        <LanguageIcon color="action" />
        <Typography variant="caption" color="text.secondary">
          Live preview unavailable (site may block captures). Open the homepage to verify.
        </Typography>
        <Link href={normalized} target="_blank" rel="noopener noreferrer" variant="caption">
          Open site
        </Link>
        <Tooltip title="Try Microlink or thumbnail preview again">
          <IconButton size="small" onClick={handleRetryMicrolink} aria-label="Retry live preview">
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>
    );
  }

  const showRetryMicrolink =
    microlinkPausedUi && !(customImageUrl && customImageUrl.trim()) && !microlinkUrl;

  return (
    <Box
      sx={{
        position: 'relative',
        height,
        borderRadius: 1,
        overflow: 'hidden',
        bgcolor: 'action.hover',
        border: 1,
        borderColor: 'divider',
      }}
    >
      {loadingMicrolink && !customImageUrl?.trim() && !microlinkUrl && !microlinkPausedUi && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'action.hover',
            zIndex: 1,
          }}
        >
          <CircularProgress size={compact ? 22 : 28} />
        </Box>
      )}
      <Box
        component="img"
        alt=""
        src={primarySrc}
        referrerPolicy="no-referrer"
        onError={handleImgError}
        sx={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          objectPosition: 'top center',
          display: 'block',
        }}
      />
      {showRetryMicrolink && (
        <Tooltip title="Try Microlink screenshot again (some sites block automated capture)">
          <IconButton
            size="small"
            onClick={handleRetryMicrolink}
            aria-label="Retry Microlink screenshot"
            sx={{
              position: 'absolute',
              bottom: 6,
              right: 6,
              zIndex: 2,
              bgcolor: 'background.paper',
              boxShadow: 1,
              '&:hover': { bgcolor: 'background.paper' },
            }}
          >
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
}
